// src/pages/GameRsvpPage.jsx
// Public one-tap RSVP page — no login required.
// URL: /game-rsvp/:gameId/:registrationId
//
// The registrationId acts as the access token (same pattern as /r/:registrationId
// parent portal). Parents reach this page via the link embedded in the 24h
// event reminder SMS.
//
// Phase 3: type-aware copy for game / practice / tournament / meeting / other.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';

import {
  CalendarDaysIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

// Design tokens — match the rest of the parent-facing surface.
// One-tap RSVP from SMS is the most-touched parent page (every game,
// every practice). Brand consistency here matters compounding.
const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ── Event type display helpers ─────────────────────────────────────── */
// All RSVP cards now use the pitch-dark hero (matches RegisterPage,
// ParentPortal, etc) for brand consistency. The TYPE_META table only
// holds the human label now — the previous per-type background colors
// (blue/emerald/purple/amber/zinc) were brand-fragmenting and the type
// is already conveyed in the heading copy ("Can {who} make it to
// practice?") and the small TYPE pill below.
const TYPE_META = {
  game:       { label: 'game'       },
  practice:   { label: 'practice'   },
  tournament: { label: 'tournament' },
  meeting:    { label: 'meeting'    },
  other:      { label: 'event'      },
};

function typeMeta(eventType) {
  return TYPE_META[eventType] || TYPE_META.game;
}

/** Human-readable title shown on the card (below the heading). */
function eventSubtitle(game) {
  if (!game) return '';
  const type = game.eventType || 'game';
  if (type === 'game') return `vs ${game.opponent || 'TBD'}`;
  return game.title || typeMeta(type).label;
}

/** Main heading on the RSVP card. */
function rsvpHeading(game, playerName) {
  const type = game?.eventType || 'game';
  const who  = playerName ? playerName : 'you';
  switch (type) {
    case 'practice':   return `Can ${who} make it to practice?`;
    case 'tournament': return `Can ${who} make it to the tournament?`;
    case 'meeting':    return `Can ${who} attend the meeting?`;
    default:           return `Can ${who} make it?`;
  }
}

/** Confirmation heading after RSVP is submitted. */
function confirmedHeading(response, eventType) {
  if (response !== 'yes') return 'Got it, no problem.';
  switch (eventType) {
    case 'practice':   return 'See you at practice! 👋';
    case 'tournament': return 'See you there! 👋';
    case 'meeting':    return 'See you at the meeting! 👋';
    default:           return 'See you there! 👋';
  }
}

/** Cancelled state copy. */
function cancelledCopy(eventType) {
  switch (eventType) {
    case 'practice':   return 'This practice has been cancelled.';
    case 'tournament': return 'This tournament event has been cancelled.';
    case 'meeting':    return 'This meeting has been cancelled.';
    default:           return 'This game has been cancelled.';
  }
}

/* ── Date/time formatters ─────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'EEEE, MMMM d'); }
  catch { return '—'; }
}

function fmtTime(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'h:mm a'); }
  catch { return ''; }
}

/* ── GameRsvpPage ────────────────────────────────────────────────────── */
export default function GameRsvpPage() {
  const { gameId, registrationId } = useParams();

  const [pageData,   setPageData]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null); // 'yes' | 'no'

  /* ── Load event + registration info ── */
  useEffect(() => {
    const fn = httpsCallable(getFunctions(), 'getGameForRsvp');
    fn({ gameId, registrationId })
      .then(r => {
        setPageData(r.data);
        if (r.data?.existingRsvp) setSubmitted(r.data.existingRsvp);
      })
      .catch(err => setError(err?.message || 'Could not load event info.'))
      .finally(() => setLoading(false));
  }, [gameId, registrationId]);

  /* ── Submit RSVP ── */
  const rsvp = async (response) => {
    setSubmitting(true);
    try {
      const fn = httpsCallable(getFunctions(), 'submitGameRsvp');
      await fn({ gameId, registrationId, response });
      setSubmitted(response);
    } catch (err) {
      setError(err?.message || 'Failed to save your RSVP. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <div className="h-12 w-12 rounded-full border-4 border-[#b5f03d]/30 border-t-[#5a8a1f] animate-spin mx-auto" />
          <p className="text-sm text-zinc-500">Loading…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 p-8 max-w-sm w-full text-center">
          <XCircleIcon className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <h2
            className="text-lg font-bold uppercase tracking-tight text-[#0d1f14] mb-2"
            style={{ fontFamily: HEAD_FONT }}
          >
            Something went wrong
          </h2>
          <p className="text-sm text-zinc-600">{error}</p>
        </div>
      </div>
    );
  }

  const { game, playerName } = pageData || {};
  const eventType  = game?.eventType || 'game';
  const meta       = typeMeta(eventType);
  const isCancelled = game?.status === 'cancelled';

  /* ── Confirmed / already responded ──
   * Lime medallion when "yes" reads as celebratory; neutral zinc when
   * "no" reads as acknowledged-but-not-celebratory. Same rounded-full
   * medallion treatment as the in-app SuccessScreen. */
  if (submitted) {
    const isYes = submitted === 'yes';
    return (
      <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 p-8 max-w-sm w-full text-center space-y-4">
          <p
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            RSVP confirmed
          </p>

          <div className={clsx(
            'inline-flex h-16 w-16 items-center justify-center rounded-full mx-auto ring-1',
            isYes ? 'bg-[#b5f03d]/15 ring-[#b5f03d]/40' : 'bg-zinc-100 ring-zinc-200',
          )}>
            {isYes
              ? <CheckCircleIcon className="h-9 w-9 text-[#5a8a1f]" />
              : <XCircleIcon     className="h-9 w-9 text-zinc-400" />
            }
          </div>

          <div>
            <h2
              className="text-xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {confirmedHeading(submitted, eventType)}
            </h2>
            {playerName && <p className="text-sm text-zinc-600 mt-1">{playerName}</p>}
          </div>

          {game && (
            <div className="rounded-xl bg-zinc-50 border border-zinc-200 px-4 py-3 text-left space-y-1.5">
              <p className="text-sm font-bold text-[#0d1f14]">{eventSubtitle(game)}</p>
              <p className="text-xs text-zinc-600 flex items-center gap-1.5">
                <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0" />
                {fmtDate(game.date)}
              </p>
              {game.date && (
                <p className="text-xs text-zinc-600 flex items-center gap-1.5">
                  <ClockIcon className="h-3.5 w-3.5 shrink-0" />
                  {fmtTime(game.date)}
                </p>
              )}
              {game.fieldName && (
                <p className="text-xs text-zinc-600 flex items-center gap-1.5">
                  <MapPinIcon className="h-3.5 w-3.5 shrink-0" />
                  {game.fieldName}
                  {game.homeAway === 'away' && game.fieldAddress && (
                    <span className="text-zinc-500"> · {game.fieldAddress}</span>
                  )}
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => setSubmitted(null)}
            className="text-xs font-bold uppercase tracking-wide text-zinc-500 hover:text-[#5a8a1f] underline transition-colors"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
          >
            Change my response
          </button>
        </div>
      </div>
    );
  }

  /* ── Main RSVP UI ── */
  return (
    <div className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] border border-zinc-200 w-full max-w-sm overflow-hidden">
        {/* Pitch-dark hero — same shape as RegisterPage / ParentPortal.
            Type variation now lives in the small lime "TYPE" pill in the
            corner instead of the entire band background. */}
        <div
          className="relative overflow-hidden px-6 py-5"
          style={{
            backgroundColor: '#0d1f14',
            backgroundImage:
              'linear-gradient(rgba(181,240,61,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(181,240,61,0.06) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        >
          <div className="relative">
            <div className="flex items-center justify-between gap-2 mb-2">
              <p
                className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#b5f03d]"
                style={{ fontFamily: HEAD_FONT }}
              >
                {meta.label}
              </p>
              {playerName && (
                <span
                  className="text-[10px] font-bold uppercase tracking-wide text-zinc-300 truncate max-w-[60%]"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  {playerName}
                </span>
              )}
            </div>
            <h1
              className="text-xl font-bold uppercase tracking-tight text-white leading-tight"
              style={{ fontFamily: HEAD_FONT }}
            >
              {rsvpHeading(game, playerName)}
            </h1>
            {eventSubtitle(game) && (
              <p className="text-sm mt-1 text-[#b5f03d]/90 font-semibold">
                {eventSubtitle(game)}
              </p>
            )}
          </div>
        </div>

        {/* Event details */}
        <div className="px-6 py-4 space-y-2 border-b border-zinc-100">
          {game?.date && (
            <>
              <div className="flex items-center gap-2 text-sm text-[#0d1f14]">
                <CalendarDaysIcon className="h-4 w-4 text-zinc-500 shrink-0" />
                <span className="font-semibold">{fmtDate(game.date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <ClockIcon className="h-4 w-4 text-zinc-500 shrink-0" />
                {fmtTime(game.date)}
              </div>
            </>
          )}
          {game?.fieldName && (
            <div className="flex items-start gap-2 text-sm text-zinc-600">
              <MapPinIcon className="h-4 w-4 text-zinc-500 shrink-0 mt-0.5" />
              <span>
                {game.fieldName}
                {game.homeAway === 'away' && game.fieldAddress && (
                  <><br /><span className="text-xs text-zinc-500">{game.fieldAddress}</span></>
                )}
              </span>
            </div>
          )}
          {/* Home/Away badge — games only. Lime for home (advantage),
              amber for away (heads up: travel needed). */}
          {eventType === 'game' && game?.homeAway && (
            <div className={clsx(
              'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border mt-1',
              game.homeAway === 'home'
                ? 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40'
                : 'bg-amber-50 text-amber-800 border-amber-200',
            )}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {game.homeAway}
            </div>
          )}
        </div>

        {/* Body */}
        {isCancelled ? (
          <div className="px-6 py-6 text-center">
            <p className="text-sm font-bold uppercase text-red-600"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              {cancelledCopy(eventType)}
            </p>
            <p className="text-xs text-zinc-500 mt-1">No RSVP needed.</p>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-3">
            <p
              className="text-[11px] text-zinc-500 text-center mb-1 font-bold uppercase tracking-wide"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Tap to let your coach know
            </p>
            <button
              onClick={() => rsvp('yes')}
              disabled={submitting}
              className="w-full py-4 rounded-2xl bg-[#b5f03d] hover:bg-[#94cc28] active:scale-[0.97] text-[#0d1f14] font-bold uppercase text-lg transition-all disabled:opacity-50 shadow-[0_1px_2px_0_rgba(13,31,20,0.08)]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              ✅  Yes, we'll be there
            </button>
            <button
              onClick={() => rsvp('no')}
              disabled={submitting}
              className="w-full py-3.5 rounded-2xl border border-zinc-200 bg-white hover:border-zinc-300 hover:bg-zinc-50 active:scale-[0.97] text-zinc-700 font-bold uppercase text-base transition-all disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
            >
              Can't make it
            </button>
            {submitting && (
              <p className="text-center text-xs text-zinc-500 animate-pulse">Saving…</p>
            )}
          </div>
        )}

        <p
          className="text-[10px] text-zinc-400 text-center pb-4"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
        >
          POWERED BY <span className="font-bold text-[#5a8a1f]">SCRIMLY</span> · getscrimly.com
        </p>
      </div>
    </div>
  );
}
